/**
 * AgentActions — 화면이 선언하는 "지금 가능한 액션" 레지스트리.
 *
 * 설계 목표: 액션형 작업의 하드코딩 최소화.
 *   기존: 액션 어휘가 step(0~3)에 묶여 ① 백엔드 ALLOWED_BY_STEP ② 프론트 executeAction switch
 *         ③ step별 칩 카탈로그 — 3곳에 중복 하드코딩. 화면 하위상태(방식=ai, 생성 전/후)를 모름.
 *   변경: 각 화면/하위 컴포넌트가 "현재 자신이 제공하는 액션"을 레지스트리에 등록한다.
 *         칩(결정적 실행)과 자유 입력(LLM이 id 선택)이 같은 레지스트리를 읽는다.
 *         → 액션 추가 = 그 액션을 수행하는 UI 옆에 디스크립터 1개. 칩은 항상 화면과 일치.
 *
 * 액션 디스크립터:
 *   {
 *     id:        "dp.ai.generate",          // 안정적 고유 id (LLM·dispatch 매핑 키)
 *     i18nKey:   "agentActDpGenAi",          // 라벨 i18n 키
 *     ko:        "AI 추천 시나리오 생성",      // l10n 미등록 폴백 라벨
 *     group:     "action",                   // "action" | "analysis" (탭)
 *     description:"AI 추천 시나리오를 생성한다", // 자유 입력 매칭용 LLM 힌트
 *     style:     "primary" | "secondary",    // 칩/버튼 스타일(선택)
 *     enabled?:  () => boolean,               // 현재 상태에서 실행 가능 여부(선택, 기본 true)
 *     run:       (args?) => void | Promise,   // 실제 수행 — 화면이 이미 가진 핸들러 재사용
 *     confirmKo?:string,                      // 실행 후 채팅 확인 메시지(선택)
 *   }
 */
import { createContext, useContext, useRef, useReducer, useCallback, useEffect, useMemo } from "react";

// ⚠️ 두 컨텍스트로 나눈다. 이 Provider 는 **앱 셸(루트)** 에 있어서, 등록/해제마다 올라가는
//    version 을 한 value 에 같이 담으면 그 value 를 구독하는 모든 소비자가 함께 재렌더된다.
//    ApiCtx 의 value 는 identity 가 영구 고정이고, 자주 바뀌는 version 만 별도 컨텍스트로 흘린다.
const AgentActionApiCtx = createContext(null);       // { register, unregister, scopes } — 고정
const AgentActionVersionCtx = createContext(0);      // 등록/해제 카운터 — 변동

export function AgentActionProvider({ children }) {
  const scopes = useRef(new Map());            // scopeId -> action[]
  const [version, bump] = useReducer(x => x + 1, 0);

  const register = useCallback((scopeId, actions) => {
    scopes.current.set(scopeId, Array.isArray(actions) ? actions : []);
    bump();
  }, []);
  const unregister = useCallback((scopeId) => {
    if (scopes.current.delete(scopeId)) bump();
  }, []);

  const api = useMemo(() => ({ register, unregister, scopes }), [register, unregister]);
  return (
    <AgentActionApiCtx.Provider value={api}>
      <AgentActionVersionCtx.Provider value={version}>{children}</AgentActionVersionCtx.Provider>
    </AgentActionApiCtx.Provider>
  );
}

/**
 * 화면/하위 컴포넌트가 "현재 제공하는 액션"을 선언한다.
 * deps 가 바뀌면(=하위상태 변경) 재등록 → 레지스트리가 항상 현재 화면을 반영.
 * actions 는 배열 또는 () => 배열(지연 평가).
 */
export function useProvideAgentActions(scopeId, actions, deps) {
  const ctx = useContext(AgentActionApiCtx);
  useEffect(() => {
    if (!ctx) return undefined;
    const list = typeof actions === "function" ? actions() : actions;
    ctx.register(scopeId, list);
    return () => ctx.unregister(scopeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** 패널이 병합·정렬된 현재 액션 목록을 읽는다(등록 순서 유지). */
export function useAgentActions() {
  const ctx = useContext(AgentActionApiCtx);
  const version = useContext(AgentActionVersionCtx);
  return useMemo(() => {
    const all = [];
    if (ctx) for (const list of ctx.scopes.current.values()) all.push(...list);
    return all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);
}
